import { apiRequest } from './http';
import type { PartnersSyncJob, PartnersSyncStatus } from './types';

// GET /api/sync-jobs/{job_id} — partners refresh job 상태 조회.
// frontend가 권한 변경 응답의 sync_job_id를 받아 1초 간격 polling.
export function fetchSyncJob(jobId: string, signal?: AbortSignal): Promise<PartnersSyncJob> {
  return apiRequest<PartnersSyncJob>(`/sync-jobs/${encodeURIComponent(jobId)}`, { signal });
}

const TERMINAL_STATUSES: ReadonlySet<PartnersSyncStatus> = new Set([
  'done',
  'failed',
  'partial',
]);

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 120_000;

/**
 * sync_job이 terminal status에 도달할 때까지 1초 간격으로 polling.
 * - timeout: 120초. 그 후엔 마지막 job 상태를 반환 (isComplete=false라도).
 * - sync_job_id가 null/undefined면 즉시 null 반환 (partners refresh disabled 환경 등).
 * - onProgress 콜백으로 매 tick에 상태 노출 가능 (UI에서 단계 표시 등).
 */
export async function waitForSyncJob(
  jobId: string | null | undefined,
  options?: { onProgress?: (job: PartnersSyncJob) => void; signal?: AbortSignal },
): Promise<PartnersSyncJob | null> {
  if (!jobId) return null;
  const startedAt = Date.now();
  while (true) {
    if (options?.signal?.aborted) {
      throw new DOMException('aborted', 'AbortError');
    }
    let job: PartnersSyncJob;
    try {
      job = await fetchSyncJob(jobId, options?.signal);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      // 일시 오류는 다음 tick에 재시도. timeout이면 throw.
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) throw err;
      await sleep(POLL_INTERVAL_MS);
      continue;
    }
    options?.onProgress?.(job);
    if (TERMINAL_STATUSES.has(job.status)) return job;
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) return job; // timeout — 마지막 상태 반환
    await sleep(POLL_INTERVAL_MS);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
